import { isResource } from '@medplum/core'
import type { Coding, List, Reference } from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import {
  type CodingValue,
  codeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { LIST_TYPE } from '#src/codeSystems.ts'
import { ExtendedList } from '#src/models/list.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

const RELEVANT_OBSERVATIONS_CODE = {
  system: LIST_TYPE,
  code: 'relevant-observations',
  display: 'Relevant Observations',
}

export class RelevantObservations extends ExtendedList {
  static isInstance(value: unknown): value is List {
    return (
      isResource(value) &&
      isOfType(value, 'List') &&
      hasCodeableConcept(value.code, RelevantObservations.code)
    )
  }

  static bySource(source: Reference): QueryTypes {
    return {
      source,
      code: RelevantObservations.code,
    }
  }

  static readonly code = codeableConcept(RELEVANT_OBSERVATIONS_CODE)

  constructor(data?: Omit<List, 'resourceType' | 'status' | 'mode'>) {
    super({
      ...data,
      code: RelevantObservations.code,
    })
  }

  add(coding: CodingValue): void {
    const existingEntry = (this.resource.entry ?? []).find((e) =>
      e.extension?.some(
        (ext) =>
          ext.url === 'coding' &&
          hasCodeableConcept(ext.valueCodeableConcept, codeableConcept(coding))
      )
    )

    if (!existingEntry) {
      this.resource.entry = [
        {
          item: {
            display: coding.display ?? coding.code,
          },
          extension: [
            {
              url: 'coding',
              valueCodeableConcept: codeableConcept(coding),
            },
          ],
        },
        ...(this.resource.entry ?? []),
      ]
    }
  }

  remove(coding: CodingValue): void {
    this.resource.entry = (this.resource.entry ?? []).filter(
      (e) =>
        !e.extension?.some(
          (ext) =>
            ext.url === 'coding' &&
            hasCodeableConcept(
              ext.valueCodeableConcept,
              codeableConcept(coding)
            )
        )
    )
  }

  setOrder(orderedCodings: Coding[]): void {
    const entries = this.resource.entry ?? []

    const entryMap = new Map<string, (typeof entries)[number]>()
    for (const entry of entries) {
      const key = entryKey(entry)
      if (key) {
        entryMap.set(key, entry)
      }
    }

    const reordered = orderedCodings.flatMap((coding) => {
      if (!coding.system || !coding.code) {
        return []
      }
      const entry = entryMap.get(`${coding.system}|${coding.code}`)
      return entry === undefined ? [] : [entry]
    })

    // Callers may reorder a filtered view of the list. Entries they left out
    // keep their index; the reordered entries fill the slots they occupied.
    // Match by coding key, not identity: under mutate() every read of an entry
    // returns a fresh proxy.
    const reorderedKeys = new Set(reordered.map(entryKey))
    let next = 0
    this.resource.entry = entries.map((entry) => {
      const key = entryKey(entry)
      if (!key || !reorderedKeys.has(key)) {
        return entry
      }
      const replacement = reordered[next] ?? entry
      next += 1
      return replacement
    })
  }

  get values(): Coding[] {
    return compact(
      this.resource.entry?.flatMap((e) =>
        e.extension
          ?.find((ext) => ext.url === 'coding')
          ?.valueCodeableConcept?.coding?.map((c) => ({
            ...c,
            display: c.display ?? c.code,
          }))
      ) ?? []
    )
  }
}

function entryKey(
  entry: NonNullable<List['entry']>[number]
): string | undefined {
  const coding = entry.extension?.find((ext) => ext.url === 'coding')
    ?.valueCodeableConcept?.coding?.[0]
  return coding?.system && coding.code
    ? `${coding.system}|${coding.code}`
    : undefined
}
